// SelectCompany — post-login company picker. Same auth card/aside as Login;
// lists the companies the logged-in user can open (users/all-companies).
// Selecting one runs the shared useSelectCompany flow (token exchange → session
// → app); "Add new company" starts the wizard.
//
// The same flow backs the in-app Switch company modal, so entering a company
// works identically whether it happens at login or mid-session.

import { useAppSelector } from '@/store/hooks';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/common';
import { appBrand } from '@/mocks/user';
import { useListCompaniesQuery } from '@/api/endpoints/companyApi';
import useSelectCompany from '@/hooks/useSelectCompany';
import logoColor from '@/assets/images/midani-logo-color.png';
import logoWhite from '@/assets/images/midani-logo-white.png';
import AuthLayout from '@/layout/AuthLayout';

const SelectCompany = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const mode = useAppSelector((state) => state.theme.mode);
  const { data: companies = [], isLoading, error } = useListCompaniesQuery();
  const { select, selecting, pendingId } = useSelectCompany();

  return (
    <AuthLayout variant='login'>
      <div className='auth-page select-company'>
        <img
          className='select-company__logo'
          src={mode === 'dark' ? logoWhite : logoColor}
          alt={appBrand.name}
        />
        <header className='auth-page__header'>
          <h1 className='auth-page__title'>{t('text_welcome')}</h1>
          <p className='auth-page__subtitle'>{t('text_select_company_to_open')}</p>
        </header>

        <ul className='select-company__list'>
          {isLoading && <li className='select-company__status'>{t('text_loading')}</li>}
          {!isLoading && error && (
            <li className='select-company__status select-company__status--error'>
              {t('error_generic')}
            </li>
          )}
          {!isLoading && !error && companies.length === 0 && (
            <li className='select-company__status'>{t('text_no_companies')}</li>
          )}
          {!isLoading &&
            !error &&
            companies.map((company) => (
              <li key={company.id}>
                <button
                  type='button'
                  className='select-company__card'
                  disabled={selecting}
                  onClick={() => select(company.id)}
                >
                  <span className='select-company__avatar'>
                    {company.name.charAt(0).toUpperCase()}
                  </span>
                  <span className='select-company__name'>{company.name}</span>
                  <span className='select-company__arrow'>
                    <Icon
                      name={pendingId === company.id ? 'loader-circle' : 'arrow-right'}
                      size={18}
                      aria-hidden='true'
                    />
                  </span>
                </button>
              </li>
            ))}
          <li>
            <button
              type='button'
              className='select-company__add'
              onClick={() => navigate('/create-company')}
            >
              <Icon name='plus' size={18} aria-hidden='true' />
              {t('button_add_new_company')}
            </button>
          </li>
        </ul>
      </div>
    </AuthLayout>
  );
};

export default SelectCompany;
